import apiClient from './api.service';

export type DocControlOrigin = 'INTERNAL' | 'EXTERNAL';
export type DocControlStatus = 'Draft' | 'Pending Approval' | 'Issued' | 'Obsolete' | 'Superseded';

export interface ControlledDocument {
  id: string;
  company_id: string;
  origin: DocControlOrigin | string;
  document_level?: string | null;
  doc_family: string;
  document_number: string;
  title: string;
  description?: string | null;
  department_code?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  finance_department_code?: string | null;
  revision: string;
  revision_date?: string | null;
  effective_date?: string | null;
  status: DocControlStatus | string;
  language?: string;
  is_translation?: number | boolean;
  translation_of_id?: string | null;
  requires_controlled_stamp?: number | boolean;
  custodian_employee_id?: string | null;
  custodian_name?: string | null;
  custodian_location?: string | null;
  prepared_by_employee_id?: string | null;
  prepared_by_name?: string | null;
  prepared_by_designation?: string | null;
  next_review_date?: string | null;
  last_external_check_date?: string | null;
  publisher_or_source?: string | null;
  obsolete_reason?: string | null;
  retain_for_reference?: number | boolean;
  retain_reason?: string | null;
  moc_id?: string | null;
  moc_ref_no?: string | null;
  approval_instance_id?: string | null;
  notes?: string | null;
  active_distribution_count?: number;
  overdue_review?: number;
  revisions?: ControlledDocumentRevision[];
  distributions?: ControlledDocumentDistribution[];
  change_requests?: ControlledDocumentChangeRequest[];
}

export interface ControlledDocumentRevision {
  id: string;
  revision: string;
  revision_date?: string | null;
  nature_of_amendment?: string | null;
  moc_id?: string | null;
  moc_ref?: string | null;
  change_request_id?: string | null;
  changed_by_name?: string | null;
  is_current?: number | boolean;
  created_at?: string;
}

export interface ControlledDocumentDistribution {
  id: string;
  recipient_employee_id?: string | null;
  recipient_user_id?: string | null;
  recipient_name: string;
  recipient_department?: string | null;
  copy_type: string;
  issued_revision: string;
  issued_at: string;
  acknowledged_at?: string | null;
  recalled_at?: string | null;
  recall_notes?: string | null;
  status: string;
}

export interface ControlledDocumentChangeRequest {
  id: string;
  cr_number: string;
  status: string;
  requester_name: string;
  description: string;
  modification_reason?: string | null;
  impact_assessment?: string | null;
  risk_notes?: string | null;
  moc_required?: number | boolean;
  moc_id?: string | null;
  review_notes?: string | null;
  implemented_revision?: string | null;
  created_at?: string;
}

export interface DocControlCatalog {
  levels: string[];
  statuses: string[];
  internal_families: Array<{
    family: string;
    label: string;
    level: string;
    pattern: string;
    requiresDept: boolean;
    controlledStamp: boolean;
  }>;
  external_families: Array<{ family: string; label: string }>;
}

export interface DocControlStats {
  internal_count: number;
  external_count: number;
  issued_count: number;
  draft_count: number;
  pending_count: number;
  obsolete_count: number;
  overdue_review_count: number;
}

function unwrap<T>(response: any): T {
  const raw = response?.data ?? response;
  return (raw?.data ?? raw) as T;
}

const documentControlService = {
  async catalog(): Promise<DocControlCatalog> {
    const response = await apiClient.get('/quality/document-control/catalog');
    return unwrap(response);
  },

  async suggestNumber(params: {
    origin?: string;
    doc_family?: string;
    department_id?: string;
    department_code?: string;
    is_translation?: boolean;
  }): Promise<{ document_number: string; pattern: string }> {
    const response = await apiClient.get('/quality/document-control/suggest-number', { params });
    return unwrap(response);
  },

  async dashboardStats(): Promise<DocControlStats> {
    const response = await apiClient.get('/quality/document-control/dashboard-stats');
    return unwrap(response);
  },

  async list(params?: Record<string, unknown>): Promise<ControlledDocument[]> {
    const response = await apiClient.get('/quality/document-control', { params });
    const data = unwrap<any>(response);
    return Array.isArray(data) ? data : [];
  },

  async get(id: string): Promise<ControlledDocument> {
    const response = await apiClient.get(`/quality/document-control/${id}`);
    return unwrap(response);
  },

  async create(payload: Record<string, unknown>): Promise<ControlledDocument> {
    const response = await apiClient.post('/quality/document-control', payload);
    return unwrap(response);
  },

  async update(id: string, payload: Record<string, unknown>): Promise<ControlledDocument> {
    const response = await apiClient.put(`/quality/document-control/${id}`, payload);
    return unwrap(response);
  },

  async remove(id: string) {
    await apiClient.delete(`/quality/document-control/${id}`);
  },

  async submit(id: string): Promise<ControlledDocument> {
    const response = await apiClient.post(`/quality/document-control/${id}/submit`);
    return unwrap(response);
  },

  async revise(
    id: string,
    payload: {
      nature_of_amendment: string;
      change_request_id?: string | null;
      moc_id?: string | null;
      moc_ref?: string | null;
    }
  ): Promise<ControlledDocument> {
    const response = await apiClient.post(`/quality/document-control/${id}/revise`, payload);
    return unwrap(response);
  },

  async obsolete(
    id: string,
    payload: { obsolete_reason?: string | null; retain_for_reference?: boolean; retain_reason?: string | null }
  ): Promise<ControlledDocument> {
    const response = await apiClient.post(`/quality/document-control/${id}/obsolete`, payload);
    return unwrap(response);
  },

  async externalCheck(id: string, check_date?: string): Promise<ControlledDocument> {
    const response = await apiClient.post(`/quality/document-control/${id}/external-check`, { check_date });
    return unwrap(response);
  },

  async distribute(id: string, recipients: Record<string, unknown>[]): Promise<ControlledDocument> {
    const response = await apiClient.post(`/quality/document-control/${id}/distribute`, { recipients });
    return unwrap(response);
  },

  async recall(id: string, distributionId: string, notes?: string): Promise<ControlledDocument> {
    const response = await apiClient.post(
      `/quality/document-control/${id}/distributions/${distributionId}/recall`,
      { notes }
    );
    return unwrap(response);
  },

  async acknowledge(id: string, distributionId: string): Promise<ControlledDocument> {
    const response = await apiClient.post(
      `/quality/document-control/${id}/distributions/${distributionId}/acknowledge`
    );
    return unwrap(response);
  },

  async createChangeRequest(id: string, payload: Record<string, unknown>): Promise<ControlledDocument> {
    const response = await apiClient.post(`/quality/document-control/${id}/change-requests`, payload);
    return unwrap(response);
  },

  async reviewChangeRequest(
    id: string,
    crId: string,
    payload: { decision: 'Approved' | 'Rejected' | 'Under Review'; review_notes?: string; moc_id?: string }
  ): Promise<ControlledDocument> {
    const response = await apiClient.post(
      `/quality/document-control/${id}/change-requests/${crId}/review`,
      payload
    );
    return unwrap(response);
  },
};

export default documentControlService;
